import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { DownloadSimple, Eye, ArrowSquareOut, CheckCircle, PenNib } from "@phosphor-icons/react";
import { api, fileDownloadUrl } from "@/lib/api";
import { TYPE_META } from "@/lib/meta";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/SignaturePad";

const NEW_RELEASE_DAYS = 14;
function isNewRelease(ts) {
  if (!ts) return false;
  const diff = Date.now() - new Date(ts).getTime();
  return diff >= 0 && diff <= NEW_RELEASE_DAYS * 24 * 60 * 60 * 1000;
}

function Markdown({ text }) {  const lines = (text || "").split("\n");
  return (
    <div className="space-y-3 font-light text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith("## ")) return <h3 key={i} className="font-head text-lg font-medium mt-4">{line.slice(3)}</h3>;
        if (line.startsWith("# ")) return <h2 key={i} className="font-head text-2xl font-light">{line.slice(2)}</h2>;
        if (/^\d+\.\s/.test(line)) return <p key={i} className="pl-4 text-muted-foreground">{line}</p>;
        if (line.trim() === "") return <div key={i} className="h-1" />;
        const bold = line.replace(/\*\*(.+?)\*\*/g, "<strong class='text-foreground font-semibold'>$1</strong>");
        return <p key={i} className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: bold }} />;
      })}
    </div>
  );
}

export default function Library() {
  const { type } = useParams();
  const meta = TYPE_META[type] || TYPE_META.doc;
  const Icon = meta.icon;
  const [viewing, setViewing] = useState(null);
  const qc = useQueryClient();

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ["resources"],
    queryFn: async () => (await api.get("/resources")).data,
  });

  const signMut = useMutation({
    mutationFn: async ({ id, png }) => api.post(`/agreements/${id}/sign`, { signature_png: png }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["resources"] }); toast.success("Agreement signed"); setViewing(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not sign agreement"),
  });

  const items = resources.filter((r) => r.type === type);
  const isPlugin = type === "plugin";

  return (
    <div className="space-y-8" data-testid={`library-${type}`}>
      <div className="flex items-center gap-4 border-b border-border pb-6">
        <div className="w-12 h-12 border border-border flex items-center justify-center bg-card">
          <Icon size={26} className="text-primary" />
        </div>
        <div>
          <h1 className="font-head text-3xl font-light tracking-tight">{meta.label}</h1>
          <p className="text-sm text-muted-foreground font-light">{meta.blurb}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground animate-pulse">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-border p-12 text-center" data-testid="library-empty">
          <p className="text-muted-foreground font-light">No {meta.label.toLowerCase()} available for your access level.</p>
        </div>
      ) : (
        <div className={`grid gap-px bg-border border border-border ${isPlugin ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="group bg-card p-6 flex flex-col hover:bg-secondary/40 transition-colors"
              data-testid={`resource-${r.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                {r.category && (
                  <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground border border-border px-2 py-0.5">
                    {r.category}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {isPlugin && isNewRelease(r.last_released_at) && (
                    <span data-testid={`new-version-badge-${r.id}`} className="font-mono text-[9px] tracking-widest uppercase bg-primary text-primary-foreground px-2 py-0.5 animate-pulse">
                      New version
                    </span>
                  )}
                  {isPlugin && r.version && (
                    <span className="font-mono text-[10px] text-primary">v{r.version}</span>
                  )}
                </div>
              </div>
              <h3 className="font-head text-lg font-medium leading-snug">{r.title}</h3>
              <p className="text-sm text-muted-foreground font-light mt-2 flex-1">{r.description}</p>

              <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
                {isPlugin ? (
                  <>
                    <a
                      href={r.file_id ? fileDownloadUrl(r.file_id, { download: true }) : r.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`download-${r.id}`}
                      className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-xs font-mono tracking-wide font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <DownloadSimple size={16} /> Download
                    </a>
                    {r.file_size && <span className="font-mono text-[11px] text-muted-foreground">{r.file_size}</span>}
                  </>
                ) : (
                  <button
                    onClick={() => setViewing(r)}
                    data-testid={`view-${r.id}`}
                    className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-mono tracking-wide hover:border-primary hover:text-primary transition-colors"
                  >
                    <Eye size={16} /> View
                  </button>
                )}
                {!isPlugin && r.requires_signature && (
                  <span data-testid={`sign-status-${r.id}`} className={`ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border ${r.signed ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                    {r.signed ? <><CheckCircle size={13} weight="fill" /> Signed</> : <><PenNib size={13} /> Signature required</>}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="resource-viewer">
          <DialogHeader>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground text-left">
              {viewing?.category}
            </span>
            <DialogTitle className="font-head text-2xl font-light text-left">{viewing?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Markdown text={viewing?.body} />
          </div>
          {viewing?.file_id && (
            <a href={fileDownloadUrl(viewing.file_id)} target="_blank" rel="noreferrer" data-testid={`view-file-${viewing.id}`} className="inline-flex items-center gap-2 mt-4 text-xs font-mono text-primary hover:underline">
              Open {viewing.file_name || "attachment"} <ArrowSquareOut size={14} />
            </a>
          )}
          {viewing?.url && (
            <a href={viewing.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-xs font-mono text-primary hover:underline">
              Open external resource <ArrowSquareOut size={14} />
            </a>
          )}
          {viewing?.requires_signature && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="agreement-signature-block">
              {viewing.signed ? (
                <div data-testid="agreement-signed" className="flex items-center gap-2 text-sm font-mono text-primary">
                  <CheckCircle size={18} weight="fill" /> Signed on {viewing.signed_at ? new Date(viewing.signed_at).toLocaleDateString() : "record"}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Draw your signature below to accept this agreement</p>
                  <SignaturePad submitting={signMut.isPending} onSubmit={(png) => signMut.mutate({ id: viewing.id, png })} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
